'use client';

import type { IImage } from '@/services/models/Media/Image';
import { use, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Image } from '@/react/components/core-ui/Image';
import {
  Breakpoints,
  Breakpoint
} from '@/react/components/core-ui/Breakpoints';
import { MainCarousel } from '@/react/components/product/MainCarousel';
import { FocusableGridContext } from '@/react/components/product/FocusableGrid';
import { classes } from '@/next-utils/css-utils/scss-utils';
import type { IPlayerSettings, IVideo } from '@/services/models/Media/Video';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { PDPVideo } from '../../../PDPVideo';
import S from './styles.module.scss';

interface IPDPGridMediaProps {
  /** Images to display in the grid. */
  images: Array<IImage>;

  /** Videos to display in the grid. */
  videos: Array<IVideo>;
}

/**
 * A component that renders a grid of medias (images and videos) that can be focused using the {@link FocusableGrid} component.
 */
export const PDPGridMedia = observer(function PDPGridMedia({
  images,
  videos
}: IPDPGridMediaProps) {
  const { setSelectedItem } = use(FocusableGridContext);

  const media = useMemo(
    () => [
      ...images.map((image) => ({ ...image, isVideo: false })),
      ...videos.map((video) => ({ ...video, isVideo: true }))
    ],
    [videos, images]
  );

  const videoSettingsConfig = ConfigurationService.getConfig(
    'product'
  ).getSetting('details.videoSettingsDesktop');

  const videoSettings: IPlayerSettings = {
    autoplay: videoSettingsConfig.autoplay.value,
    muted: videoSettingsConfig.muted.value,
    loop: videoSettingsConfig.loop.value
  };

  if (media.length === 0) {
    return null;
  }

  if (media.length === 1) {
    return (
      <div key={media[0].uuid} className={S.item}>
        {media[0].isVideo ? (
          <PDPVideo video={{ ...media[0], settings: videoSettings }} />
        ) : (
          <Image key={media[0].uuid} image={media[0]} />
        )}
      </div>
    );
  }

  return (
    <Breakpoints>
      <Breakpoint media="xxs:sm">
        <MainCarousel images={images} videos={videos} />
      </Breakpoint>
      <Breakpoint default>
        <div className={S.grid}>
          {media.map((media, i) =>
            media.isVideo ? (
              <div key={media.uuid}>
                <PDPVideo video={{ ...media, settings: videoSettings }} />
              </div>
            ) : (
              <div
                key={media.uuid}
                onClick={() => {
                  setSelectedItem(i);
                }}
                tabIndex={0}
                className={classes(S.item, S.zoomable)}
                role="button"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSelectedItem(i);
                  }
                }}
              >
                <Image key={media.uuid} image={media} />
              </div>
            )
          )}
        </div>
      </Breakpoint>
    </Breakpoints>
  );
});
